import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GitCompare, Check, ExternalLink, Heart, CheckCircle } from 'lucide-react';
import { useSwipes } from '../../hooks/useBands';
import { CURRENT_CARNIVAL_YEAR } from '../../lib/config';
import { Card, Button } from '../../components/ui';
import type { Band } from '../../types/database';
import styles from './Matches.module.css';

export function Matches() {
  const navigate = useNavigate();
  const { userMatches, loading, recordSwipe, refetch } = useSwipes();
  const [compareMode, setCompareMode] = useState(false);
  const [selectedBands, setSelectedBands] = useState<Band[]>([]);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  // Separate connected (decided) from matched (interested)
  const connectedBands = userMatches.filter((m) => m.action === 'connect');
  const matchedBands = userMatches.filter((m) => m.action === 'match');

  const toggleBandSelection = (band: Band) => {
    if (selectedBands.find((b) => b.id === band.id)) {
      setSelectedBands(selectedBands.filter((b) => b.id !== band.id));
    } else if (selectedBands.length < 2) {
      setSelectedBands([...selectedBands, band]);
    }
  };

  const handleConnect = async (bandId: string) => {
    setConnectingId(bandId);
    await recordSwipe(bandId, 'connect');
    await refetch(); // Refresh the matches list
    setConnectingId(null);
  };

  const formatPrice = (min?: number | null, max?: number | null) => {
    if (!min && !max) return 'Price TBA';
    if (min === max) return `$${min}`;
    return `$${min} - $${max}`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  const hasAnyMatches = connectedBands.length > 0 || matchedBands.length > 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/discover')}>
          <ArrowLeft size={24} />
        </button>
        <div className={styles.headerTitle}>
          <h1>My Bands</h1>
          <span className={styles.yearBadge}>Carnival {CURRENT_CARNIVAL_YEAR}</span>
        </div>
        {hasAnyMatches && (
          <button
            className={`${styles.compareToggle} ${compareMode ? styles.active : ''}`}
            onClick={() => {
              setCompareMode(!compareMode);
              setSelectedBands([]);
            }}
          >
            <GitCompare size={16} />
            {compareMode ? `${selectedBands.length}/2` : 'Compare 2'}
          </button>
        )}
        {!hasAnyMatches && <div style={{ width: 80 }} />}
      </header>

      {!hasAnyMatches ? (
        <div className={styles.empty}>
          <Heart size={64} className={styles.emptyIcon} />
          <h2>No matches yet</h2>
          <p>Start swiping to find bands that match your vibe!</p>
          <Button onClick={() => navigate('/discover')}>Start Matching</Button>
        </div>
      ) : (
        <>
          {/* Connected bands (decided) */}
          {connectedBands.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <CheckCircle size={18} />
                My Decision ({connectedBands.length})
              </h2>
              <p className={styles.sectionSubtitle}>Bands you've decided to register with</p>
              <div className={styles.grid}>
                {connectedBands.map(({ band }) => (
                  <BandListItem
                    key={band.id}
                    band={band}
                    formatPrice={formatPrice}
                    compareMode={compareMode}
                    isSelected={!!selectedBands.find((b) => b.id === band.id)}
                    onSelect={() => toggleBandSelection(band)}
                    isConnected
                  />
                ))}
              </div>
            </section>
          )}

          {/* Matched bands (interested) */}
          {matchedBands.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Heart size={18} />
                Interested ({matchedBands.length})
              </h2>
              <p className={styles.sectionSubtitle}>Tap the check to confirm your decision</p>
              <div className={styles.grid}>
                {matchedBands.map(({ band }) => (
                  <BandListItem
                    key={band.id}
                    band={band}
                    formatPrice={formatPrice}
                    compareMode={compareMode}
                    isSelected={!!selectedBands.find((b) => b.id === band.id)}
                    onSelect={() => toggleBandSelection(band)}
                    onConnect={() => handleConnect(band.id)}
                    isConnecting={connectingId === band.id}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Compare panel */}
          {compareMode && selectedBands.length === 2 && (
            <ComparePanel bands={selectedBands} formatPrice={formatPrice} />
          )}

          {compareMode && selectedBands.length < 2 && (
            <div className={styles.compareHint}>
              Tap {2 - selectedBands.length} band{selectedBands.length === 1 ? '' : 's'} to compare
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BandListItem({
  band,
  formatPrice,
  compareMode,
  isSelected,
  onSelect,
  isConnected,
  onConnect,
  isConnecting,
}: {
  band: Band;
  formatPrice: (min?: number | null, max?: number | null) => string;
  compareMode: boolean;
  isSelected: boolean;
  onSelect: () => void;
  isConnected?: boolean;
  onConnect?: () => void;
  isConnecting?: boolean;
}) {
  const vibeLabels: Record<string, string> = {
    bacchanal: 'Bacchanal',
    pretty: 'Pretty Mas',
    music: 'Music',
    social: 'Social',
    cultural: 'Cultural',
  };

  return (
    <Card
      className={`${styles.bandItem} ${compareMode ? styles.selectable : ''} ${isSelected ? styles.selected : ''} ${isConnected ? styles.connected : ''}`}
      onClick={compareMode ? onSelect : undefined}
      padding="none"
    >
      <div className={styles.bandImage}>
        {band.hero_image_url ? (
          <img src={band.hero_image_url} alt={band.name} />
        ) : (
          <div className={styles.bandPlaceholder}>{band.name[0]}</div>
        )}
        {compareMode && (
          <div className={styles.checkbox}>
            {isSelected && <Check size={14} />}
          </div>
        )}
        {isConnected && !compareMode && (
          <div className={styles.connectedBadge}>
            <CheckCircle size={14} />
          </div>
        )}
      </div>
      <div className={styles.bandInfo}>
        <h3 className={styles.bandName}>{band.name}</h3>
        <p className={styles.bandPrice}>{formatPrice(band.price_min, band.price_max)}</p>
        <div className={styles.bandTags}>
          {band.vibe_tags?.slice(0, 2).map((tag) => (
            <span key={tag} className={styles.bandTag}>
              {vibeLabels[tag] || tag}
            </span>
          ))}
        </div>
      </div>
      {!compareMode && !isConnected && onConnect && (
        <button
          className={styles.connectBtn}
          onClick={(e) => {
            e.stopPropagation();
            onConnect();
          }}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <div className={styles.miniSpinner} />
          ) : (
            <>
              <Check size={16} />
              <span>I've Decided!</span>
            </>
          )}
        </button>
      )}
      {!compareMode && band.website_url && (
        <a
          href={band.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.visitBtn}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink size={14} />
        </a>
      )}
    </Card>
  );
}

function ComparePanel({
  bands,
  formatPrice,
}: {
  bands: Band[];
  formatPrice: (min?: number | null, max?: number | null) => string;
}) {
  const [band1, band2] = bands;

  const vibeLabels: Record<string, string> = {
    bacchanal: 'Bacchanal',
    pretty: 'Pretty Mas',
    music: 'Music',
    social: 'Social',
    cultural: 'Cultural',
  };

  return (
    <div className={styles.comparePanel}>
      <h3>Compare</h3>
      <div className={styles.compareGrid}>
        {/* Headers */}
        <div className={styles.compareHeader}>{band1.name}</div>
        <div className={styles.compareLabel}>vs</div>
        <div className={styles.compareHeader}>{band2.name}</div>

        {/* Price */}
        <div className={styles.compareValue}>{formatPrice(band1.price_min, band1.price_max)}</div>
        <div className={styles.compareLabel}>Price</div>
        <div className={styles.compareValue}>{formatPrice(band2.price_min, band2.price_max)}</div>

        {/* Vibes */}
        <div className={styles.compareValue}>
          {band1.vibe_tags?.map((v) => vibeLabels[v]).join(', ') || '-'}
        </div>
        <div className={styles.compareLabel}>Vibe</div>
        <div className={styles.compareValue}>
          {band2.vibe_tags?.map((v) => vibeLabels[v]).join(', ') || '-'}
        </div>

        {/* Sections */}
        <div className={styles.compareValue}>{band1.section_count || '?'}</div>
        <div className={styles.compareLabel}>Sections</div>
        <div className={styles.compareValue}>{band2.section_count || '?'}</div>

        {/* Inclusives */}
        <div className={styles.compareValue}>{band1.inclusives?.length || 0}</div>
        <div className={styles.compareLabel}>Inclusives</div>
        <div className={styles.compareValue}>{band2.inclusives?.length || 0}</div>
      </div>
    </div>
  );
}
